import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import ConfirmationModal from '../components/ConfirmationModal';
import { 
  Beaker, 
  Play, 
  Download, 
  Trash2, 
  RefreshCw, 
  CheckCircle2, 
  AlertTriangle,
  Clock,
  FileCode,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Code2,
  Terminal,
  Activity,
  TrendingUp,
  MoreHorizontal,
  X,
  Copy,
  PlayCircle,
  Sparkles,
  Eye
} from 'lucide-react';

const rawApiUrl = import.meta.env.VITE_API_URL || 'https://view-gate.vercel.app';
const API_URL = rawApiUrl.endsWith('/api') ? rawApiUrl.slice(0, -4) : rawApiUrl;

// Stat Card Component matching dashboard design
const StatCard = ({ label, value, subtext, icon: Icon, colorClass, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="glass-card p-6 border-white/5 relative overflow-hidden group hover:bg-white/[0.03] transition-all"
  >
    <div className={`absolute -right-4 -top-4 w-24 h-24 rounded-full blur-3xl opacity-10 group-hover:opacity-20 transition-opacity ${colorClass}`} />
    <div className="flex items-start justify-between mb-4">
      <div className="p-2.5 rounded-xl bg-white/5 text-slate-400 group-hover:text-white transition-colors border border-white/5">
        <Icon size={20} />
      </div>
    </div>
    <div className="space-y-1">
      <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest leading-none mb-1">{label}</div>
      <div className="text-3xl font-display font-black text-slate-100">{value}</div>
      {subtext && <div className="text-[10px] text-slate-400 font-medium opacity-60">{subtext}</div>}
    </div>
  </motion.div>
);

// Step Type Badge
const StepTypeBadge = ({ type }) => {
  const configs = {
    click: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: '●' },
    fill: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20', icon: '✎' },
    goto: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: '→' },
    assertVisible: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: '◉' },
    press: { bg: 'bg-pink-500/10', text: 'text-pink-400', border: 'border-pink-500/20', icon: '⌨' },
    wait: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20', icon: '◷' },
    select: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/20', icon: '☰' }
  };
  const config = configs[type] || configs.wait;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${config.bg} ${config.text} ${config.border}`}>
      <span className="opacity-70">{config.icon}</span>
      {type}
    </span>
  );
};

// Status Badge
const StatusBadge = ({ status }) => {
  const configs = {
    active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', dot: 'bg-emerald-400' },
    degraded: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', dot: 'bg-amber-400' },
    needs_review: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20', dot: 'bg-rose-400' }
  };
  const config = configs[status] || configs.needs_review;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${config.bg} ${config.text} ${config.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {status.replace('_', ' ')}
    </span>
  );
};

// Generated Badge - shows if flow was processed by MCP
const GeneratedBadge = ({ isGenerated }) => {
  const { t } = useLanguage();
  
  if (isGenerated) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-indigo-500/10 text-indigo-400 border-indigo-500/20" title={t('e2eMCPProcessed')}>
        <Beaker className="w-3 h-3" />
        {t('e2eMCPProcessed')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-slate-500/10 text-slate-400 border-slate-500/20" title={t('e2eCaptured')}>
      <Activity className="w-3 h-3" />
      {t('e2eCaptured')}
    </span>
  );
};

// Empty State Component
const EmptyState = ({ icon: Icon, title, description, action }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="text-center py-16 px-4"
  >
    <div className="relative inline-flex items-center justify-center mb-6">
      <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
      <div className="relative p-4 rounded-2xl bg-white/5 border border-white/10">
        <Icon className="w-8 h-8 text-slate-400" />
      </div>
    </div>
    <h3 className="text-lg font-bold text-slate-200 mb-2">{title}</h3>
    <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">{description}</p>
    {action}
  </motion.div>
);

// Flow Card Component
const FlowCard = ({ flow, isExpanded, onToggle, onExport, onDelete, onMarkSeen }) => {
  const { t } = useLanguage();
  const confidence = Math.round((flow.metadata?.confidence || 0) * 100);
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card border-white/5 overflow-hidden group"
    >
      {/* Header */}
      <div
        className="p-5 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <button className="p-1 rounded-lg hover:bg-white/5 text-slate-500 transition-colors">
            <motion.div
              animate={{ rotate: isExpanded ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronRight className="w-5 h-5" />
            </motion.div>
          </button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/5 border border-white/10">
              <Code2 className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h3 className="font-bold text-slate-200 group-hover:text-white transition-colors">{flow.name}</h3>
              <div className="flex items-center gap-3 text-[10px] text-slate-500 font-medium">
                <span className="flex items-center gap-1">
                  <Activity className="w-3 h-3" />
                  {flow.steps?.length || 0} {t('e2eStepsCount')}
                </span>
                {(() => {
                  const newSteps = flow.steps?.filter(s => s.isNew).length || 0;
                  return newSteps > 0 ? (
                    <span className="flex items-center gap-1 text-indigo-400 font-bold">
                      <Sparkles className="w-3 h-3" />
                      {newSteps} new
                    </span>
                  ) : null;
                })()}
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  {confidence}% {t('e2eConfidence')}
                </span>
                <span>•</span>
                <span>{new Date(flow.metadata?.updatedAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <StatusBadge status={flow.status} />
          <GeneratedBadge isGenerated={flow.isGenerated} />
          
          <div className="flex items-center gap-1 ml-4">
            <button
              onClick={(e) => { e.stopPropagation(); onExport(flow._id); }}
              className="p-2 rounded-lg text-slate-400 hover:text-indigo-400 hover:bg-indigo-500/10 transition-all"
              title={t('e2eExport')}
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(flow._id); }}
              className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
              title={t('e2eDelete')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Steps */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="border-t border-white/5 bg-black/20"
          >
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">{t('e2eTestSteps')}</h4>
                <div className="flex items-center gap-3">
                  {(() => {
                    const newSteps = flow.steps?.filter(s => s.isNew).length || 0;
                    return newSteps > 0 ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkSeen?.(flow._id);
                        }}
                        className="flex items-center gap-1.5 text-[10px] font-medium text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 px-3 py-1.5 rounded-lg border border-indigo-500/20 transition-all"
                      >
                        <Eye className="w-3 h-3" />
                        Mark {newSteps} as seen
                      </button>
                    ) : null;
                  })()}
                  <span className="text-[10px] text-slate-500">{flow.steps?.length} {t('e2eStepsTotal')}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                {flow.steps?.map((step, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`flex items-center gap-4 p-3 rounded-xl border transition-colors group/step ${
                      step.isNew 
                        ? 'bg-indigo-500/10 border-indigo-500/30 hover:bg-indigo-500/15' 
                        : 'bg-white/[0.02] border-white/5 hover:bg-white/[0.04]'
                    }`}
                  >
                    <span className={`text-[10px] font-black w-6 text-center ${
                      step.isNew ? 'text-indigo-400' : 'text-slate-600'
                    }`}>
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    
                    <StepTypeBadge type={step.type} />
                    
                    <span className={`text-sm flex-1 font-medium ${
                      step.isNew ? 'text-indigo-200' : 'text-slate-300'
                    }`}>
                      {step.description}
                    </span>
                    
                    <div className="flex items-center gap-3">
                      {step.isNew && (
                        <span className="text-[9px] font-black uppercase tracking-wider text-indigo-400 bg-indigo-500/20 px-2 py-1 rounded border border-indigo-500/30">
                          NEW
                        </span>
                      )}
                      <span className="text-[10px] text-slate-500 font-mono bg-white/5 px-2 py-1 rounded">
                        {step.selectorType}
                      </span>
                      {step.confidence < 0.8 && (
                        <div className="flex items-center gap-1 text-[10px] text-amber-400 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/20">
                          <AlertTriangle className="w-3 h-3" />
                          {t('e2eLowConfidence')}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Export Modal Component
const ExportModal = ({ code, onClose }) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code?.testCode || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="glass-card w-full max-w-4xl max-h-[85vh] flex flex-col border-white/10"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
              <Terminal className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h3 className="font-bold text-slate-200">{t('e2eExportTitle')}</h3>
              <p className="text-[10px] text-slate-500">{t('e2eExportReady')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Code */}
        <div className="flex-1 overflow-auto p-5">
          <div className="relative">
            <pre className="bg-[#0a0a0a] text-slate-300 p-5 rounded-xl text-sm overflow-x-auto font-mono leading-relaxed border border-white/5">
              <code>{code?.testCode || `// ${t('e2eNoCode')}`}</code>
            </pre>
            <button
              onClick={handleCopy}
              className="absolute top-3 right-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all border border-white/10"
            >
              {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-5 border-t border-white/5 flex justify-end gap-3">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium transition-colors"
          >
            {copied ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                {t('e2eCopied')}
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                {t('e2eCopyClipboard')}
              </>
            )}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white font-medium transition-colors"
          >
            {t('e2eClose')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component
export default function E2E() {
  const { activeProject: selectedProject } = useProject();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedFlow, setExpandedFlow] = useState(null);
  const [exportedCode, setExportedCode] = useState(null);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState(null);

  // Dashboard uses JWT token, not API key
  const token = localStorage.getItem('token');

  useEffect(() => {
    console.log('[E2E Dashboard] useEffect triggered. selectedProject:', selectedProject?._id, 'token present:', !!token);
    if (selectedProject && token) {
      console.log('[E2E Dashboard] Calling fetchFlows...');
      fetchFlows();
    } else {
      console.log('[E2E Dashboard] Skipping fetchFlows - missing:', !selectedProject ? 'selectedProject' : 'token');
    }
  }, [selectedProject, token]);

  const fetchFlows = async () => {
    console.log('[E2E Dashboard] fetchFlows called. selectedProject._id:', selectedProject?._id, 'token present:', !!token);
    if (!selectedProject?._id || !token) {
      console.log('[E2E Dashboard] Early return - missing requirements');
      setFlows([]);
      return;
    }
    setLoading(true);
    setError(null);
    const url = `${API_URL}/api/e2e/flows?projectId=${selectedProject._id}`;
    console.log('[E2E Dashboard] Raw API_URL:', rawApiUrl, 'Processed API_URL:', API_URL);
    console.log('[E2E Dashboard] Fetching from:', url);
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': 'dashboard' }
      });
      console.log('[E2E Dashboard] Response status:', response.status);
      if (!response.ok) throw new Error('Failed to fetch flows');
      const data = await response.json();
      setFlows(data.flows || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (flowId) => {
    setFlowToDelete(flowId);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setFlowToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!flowToDelete) return;
    try {
      const response = await fetch(`${API_URL}/api/e2e/flows/${flowToDelete}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': 'dashboard' }
      });
      if (!response.ok) throw new Error('Failed to delete flow');
      setFlows(flows.filter(f => f._id !== flowToDelete));
      closeDeleteModal();
    } catch (err) {
      setError(err.message);
      closeDeleteModal();
    }
  };

  const exportFlow = async (flowId) => {
    try {
      const response = await fetch(`${API_URL}/api/e2e/flows/${flowId}/export`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': 'dashboard' }
      });
      if (!response.ok) throw new Error('Failed to export flow');
      const data = await response.json();
      setExportedCode(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const markStepsAsSeen = async (flowId) => {
    try {
      const response = await fetch(`${API_URL}/api/e2e/flows/${flowId}/mark-seen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': 'dashboard' }
      });
      if (!response.ok) throw new Error('Failed to mark steps as seen');
      
      // Update local state to clear isNew flags
      setFlows(flows.map(f => {
        if (f._id === flowId) {
          return {
            ...f,
            steps: f.steps?.map(s => ({ ...s, isNew: false }))
          };
        }
        return f;
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const stats = {
    total: flows.length,
    active: flows.filter(f => f.status === 'active').length,
    degraded: flows.filter(f => f.status === 'degraded').length,
    needsReview: flows.filter(f => f.status === 'needs_review').length
  };

  const avgConfidence = flows.length > 0 
    ? Math.round(flows.reduce((acc, f) => acc + (f.metadata?.confidence || 0), 0) / flows.length * 100)
    : 0;

  return (
    <div className="h-full">
      {/* Header */}
      <div className="relative z-[60] flex items-start justify-between gap-6 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 shadow-[0_0_30px_rgba(99,102,241,0.15)]">
            <Beaker size={22} />
          </div>
          <div>
            <h1 className="text-3xl font-display font-black text-slate-100 italic">{t('e2eTestFlows')}</h1>
            <p className="text-slate-500 text-sm font-medium mt-1">{t('e2eTestFlowsDesc')}</p>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl text-rose-400 text-sm flex items-center gap-2"
          >
            <AlertTriangle className="w-4 h-4" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto hover:text-rose-300">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Grid */}
      <div className="relative z-50 glass-panel border border-white/5 rounded-[28px] p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          label={t('e2eTotalFlows')}
          value={stats.total} 
          subtext={t('e2eTestFlowList')}
          icon={Code2}
          colorClass="bg-indigo-500"
          delay={0}
        />
        <StatCard 
          label={t('e2eActive')}
          value={stats.active}
          subtext={t('e2eReadyForCICD')}
          icon={CheckCircle2}
          colorClass="bg-emerald-500"
          delay={0.1}
        />
        <StatCard 
          label={t('e2eNeedsReview')}
          value={stats.needsReview}
          subtext={t('e2eRequiresAttention')}
          icon={Clock}
          colorClass="bg-rose-500"
          delay={0.2}
        />
        <StatCard 
          label={t('e2eAvgConfidence')}
          value={`${avgConfidence}%`}
          subtext={t('e2eSelectorReliability')}
          icon={Activity}
          colorClass="bg-amber-500"
          delay={0.3}
        />
        </div>
      </div>

      {/* Content - Test Flows List */}
      <div className="relative z-10 glass-panel border border-white/5 rounded-[28px] p-6 overflow-hidden mb-12">
        <div className="flex items-center justify-between mb-5">
          <div className="text-sm font-black text-white uppercase tracking-widest">{t('e2eTestFlowList')}</div>
          <div className="flex items-center gap-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {loading ? t('loading') : `${flows.length} ${t('e2eFlows')}`}
            </div>
            <button
              onClick={fetchFlows}
              disabled={loading || !selectedProject}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              {t('e2eRefresh')}
            </button>
          </div>
        </div>

        {!selectedProject ? (
          <EmptyState
            icon={Briefcase}
            title={t('e2eNoProjectSelected')}
            description={t('e2eSelectProjectDesc')}
          />
        ) : flows.length === 0 ? (
          <EmptyState
            icon={Beaker}
            title={t('e2eNoFlowsYet')}
            description={t('e2eNoFlowsDesc')}
            action={
              <div className="flex items-center justify-center gap-2 text-[10px] text-slate-500">
                <span className="p-2 rounded-lg bg-white/5 border border-white/10">
                  <Beaker className="w-4 h-4 text-indigo-400" />
                </span>
                <span>{t('e2eClickBeakerHint')}</span>
              </div>
            }
          />
        ) : (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-3"
          >
            {flows.map((flow, idx) => (
              <FlowCard
                key={flow._id}
                flow={flow}
                isExpanded={expandedFlow === flow._id}
                onToggle={() => setExpandedFlow(expandedFlow === flow._id ? null : flow._id)}
                onExport={exportFlow}
                onDelete={openDeleteModal}
                onMarkSeen={markStepsAsSeen}
              />
            ))}
          </motion.div>
        )}
      </div>

      {/* Export Modal */}
      <AnimatePresence>
        {exportedCode && (
          <ExportModal 
            code={exportedCode} 
            onClose={() => setExportedCode(null)} 
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('e2eDeleteTitle')}
        message={t('e2eDeleteDescription')}
        type="danger"
        confirmText={t('delete')}
        t={t}
      />
    </div>
  );
}
